import {
  animate,
  SequenceOptions,
  useMotionValue,
  useTransform,
} from 'motion/react'
import { FC, useEffect } from 'react'
import { formatNumber } from 'src/helpers/FormatHelper'
import { easeTransition } from 'src/hooks/useAppear'

import * as SC from './styled'

export type KeyFigureProps = {
  delay?: number
  unit?: string[]
  value: string
  withMotion?: boolean
}

const KeyFigure: FC<KeyFigureProps> = ({ delay, unit, value, withMotion }) => {
  const { prefix, suffix, targetValue, isNonNumeric } = useKeyFigureValue(value)

  const targetWidth = `${targetValue}`.length

  const motionValue = useMotionValue(0)
  const progressValue = useTransform(motionValue, (latest) =>
    formatNumber(latest, 0)
  )

  useEffect(() => {
    if (!withMotion || isNonNumeric) return

    const options: SequenceOptions = {
      ...easeTransition,
      delay,
      duration: 2.5,
    }
    const animation = animate(motionValue, targetValue, options)
    return animation.stop
  }, [delay, isNonNumeric, motionValue, targetValue, withMotion])

  if (isNonNumeric) {
    return (
      <SC.KeyFigure>
        <SC.Value $mobile>{value}</SC.Value>
        <SC.Value>{value}</SC.Value>
        {unit && unit.length > 0 && <SC.Unit>{unit.join(' ')}</SC.Unit>}
      </SC.KeyFigure>
    )
  }

  return (
    <SC.KeyFigure>
      {prefix}
      <SC.Value $mobile>{formatNumber(targetValue, 0)}</SC.Value>
      <SC.Value $length={targetWidth}>
        {!withMotion ? formatNumber(targetValue, 0) : progressValue}
      </SC.Value>
      {suffix}
      {unit && unit.length > 0 && <SC.Unit>{unit.join(' ')}</SC.Unit>}
    </SC.KeyFigure>
  )
}

const useKeyFigureValue = (value: string) => {
  const targetValue = parseInt(
    value.replace(' ', '').replace(/\D/g, '').trim(),
    10
  )
  const isNonNumeric = isNaN(targetValue)
  const targetUnit = value.replace(/\d/g, '')

  const isUnitBefore = value.indexOf(targetUnit) < 0
  const prefix = isUnitBefore ? targetUnit : ''
  const suffix = !isUnitBefore ? targetUnit : ''

  return { prefix, suffix, targetValue, isNonNumeric }
}

export default KeyFigure
